import { useEffect, useLayoutEffect, useMemo, useReducer, useRef, useState, type ReactNode } from "react";

import {
  CAMPO,
  ataqueParaDireita,
  basesDoLado,
  estadoInicial,
  orientar,
  roteiroDosLances,
  type Acao,
  type EstadoDoRoteiro,
  type Lado,
  type Marca,
  type Ponto,
} from "./coreografia";
import type { LanceDaPartida, PartidaView } from "./tipos";

/**
 * O campo ao vivo: os vinte e dois, a bola e os dois gols.
 *
 * Executa o roteiro que a coreografia escreve (`coreografia.ts`) com uma
 * física simples de futebol de botão: cada jogador corre para onde precisa
 * estar com velocidade de gente; a bola está no pé de alguém, ou voando de um
 * pé para outro (quem recebe vai ao encontro e ela chega nele), ou morta
 * esperando alguém a buscar. Passe, condução, desarme e chute são ações que
 * só terminam quando acontecem de verdade no campo, e o tempo do trecho é
 * repartido acelerando ou desacelerando o relógio da física, nunca teleportando
 * ninguém.
 *
 * As posições não passam pelo React: são vinte e três transformações por
 * quadro, escritas direto no SVG. O React só monta as fichas e troca número e
 * nome quando alguém entra ou sai.
 *
 * No celular o campo deita: a mesma partida, com os gols em cima e embaixo,
 * para usar a altura da tela em vez de espremer o desenho na largura.
 */

const MEIO = { x: CAMPO.largura / 2, y: CAMPO.altura / 2 };
const limitar = (v: number, de: number, ate: number): number => Math.min(ate, Math.max(de, v));
const dist = (a: Ponto, b: Ponto): number => Math.hypot(a.x - b.x, a.y - b.y);

/** Velocidades, em metros por segundo de jogo. */
const VEL = { corre: 7.2, conduz: 5.4, goleiro: 5.6, passe: 15, passeAlto: 20, chute: 27, chuteAlto: 22 } as const;

interface Ficha {
  chave: string;
  lado: Lado;
  vaga: number;
  id: string | null;
  x: number;
  y: number;
  /** Para onde a ação atual manda (conduzir, recolher, desarmar). */
  alvoDaAcao: Ponto | null;
  /** Para onde uma ordem de correr mandou; some ao chegar. */
  alvoDeCorrida: Ponto | "bola" | null;
  /** A última direção do movimento, para a bola ficar à frente do pé. */
  dx: number;
  dy: number;
  marca: Marca | null;
  marcaAte: number;
  fora: boolean;
}

interface Voo {
  de: Ponto;
  para: Ponto | { id: string };
  t: number;
  dur: number;
  alto: boolean;
  chute: Extract<Acao, { tipo: "chute" }> | null;
}

interface Bola {
  x: number;
  y: number;
  dono: string | null;
  voo: Voo | null;
  altura: number;
}

interface Leva {
  acoes: Acao[];
  /** Quantos segundos de jogo passam por segundo real nesta leva. */
  escala: number | null;
}

interface AcaoEmCurso {
  acao: Acao;
  inicio: number;
  limite: number;
}

const GOLEIRO: Record<Lado, { fundo: string; borda: string; texto: string }> = {
  home: { fundo: "#f5c542", borda: "#3a2a00", texto: "#1a1200" },
  away: { fundo: "#4ea1ff", borda: "#0b1f3a", texto: "#06111f" },
};

/** Claro ou escuro para o número, conforme a cor da camisa. */
function textoSobre(cor: string): string {
  const hex = cor.replace("#", "");
  if (hex.length < 6) return "#fff";
  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);
  return (r * 299 + g * 587 + b * 114) / 1000 > 150 ? "#101410" : "#ffffff";
}

export function CampoAoVivo({
  partida,
  velocidade,
  orcamentoMs,
  parado,
  intervalo = false,
  aoMostrar,
  aoDesenhar,
}: {
  partida: PartidaView;
  /**
   * Quantos segundos de jogo passam por segundo real: 1 é o ritmo natural, 2
   * e 4 aceleram tudo por igual. Zero desenha a leva na hora, sem animar.
   */
  velocidade: number;
  /**
   * Quanto tempo real cada leva pode levar, quando quem manda os lances não
   * espera o desenho (o online). A física acelera o que for preciso para
   * caber, nunca abaixo da velocidade escolhida. Sem isto, o trecho dura o
   * que precisa.
   */
  orcamentoMs?: number;
  /** Pausa, intervalo ou substituição: sem leva nova, nada anda. */
  parado: boolean;
  /**
   * O intervalo: os times trocam de lado ali mesmo, no vestiário, e a dupla
   * da saída de jogo de cada lado já volta para perto do círculo central.
   * Assim o segundo tempo começa do jeito que se espera, com a bola no meio
   * e os lados invertidos, e não com o jogo simplesmente seguindo.
   */
  intervalo?: boolean;
  /** Chamado quando o roteiro chega a um lance: é a hora de mostrar o lance e tocar o som. */
  aoMostrar?: (lance: LanceDaPartida) => void;
  /**
   * Avisa quando começa e quando termina de desenhar. No começo diz quantos
   * milissegundos reais a leva deve levar, para o relógio da página correr junto.
   */
  aoDesenhar?: (desenhando: boolean, previstoMs?: number) => void;
}): ReactNode {
  const caixa = useRef<HTMLDivElement | null>(null);
  const [vertical, definirVertical] = useState(false);
  const verticalRef = useRef(false);
  verticalRef.current = vertical;

  const fichas = useRef<Ficha[]>([]);
  const elementos = useRef(new Map<string, SVGGElement>());
  const bolaEl = useRef<SVGGElement | null>(null);
  const bolaCirc = useRef<SVGCircleElement | null>(null);
  const bolaSombra = useRef<SVGEllipseElement | null>(null);
  const bola = useRef<Bola>({ x: MEIO.x, y: MEIO.y, dono: null, voo: null, altura: 0 });
  const levas = useRef<Leva[]>([]);
  const emCurso = useRef<AcaoEmCurso | null>(null);
  const roteiro = useRef<EstadoDoRoteiro>(estadoInicial());
  const segundoTempo = useRef(false);
  const tempo = useRef(0);
  const vistos = useRef(0);
  const celebrando = useRef<{ id: string; ate: number } | null>(null);
  /** A volta à formação antes de uma saída de jogo: todo mundo na posição, sem bola puxando. */
  const voltando = useRef(false);
  const desenhando = useRef(false);
  const [, redesenhar] = useReducer((n: number) => n + 1, 0);

  const partidaRef = useRef(partida);
  partidaRef.current = partida;
  const aoMostrarRef = useRef(aoMostrar);
  aoMostrarRef.current = aoMostrar;
  const aoDesenharRef = useRef(aoDesenhar);
  aoDesenharRef.current = aoDesenhar;
  const paradoRef = useRef(parado);
  paradoRef.current = parado;
  const velocidadeRef = useRef(velocidade);
  velocidadeRef.current = velocidade;
  const orcamentoRef = useRef(orcamentoMs);
  orcamentoRef.current = orcamentoMs;
  const intervaloRef = useRef(intervalo);
  intervaloRef.current = intervalo;

  const bases = useMemo(
    () => ({ home: basesDoLado(partida.home), away: basesDoLado(partida.away) }),
    [partida.home.formation, partida.home.slots, partida.home.custom, partida.away.formation, partida.away.slots, partida.away.custom],
  );
  const basesRef = useRef(bases);
  basesRef.current = bases;

  // Deitar ou não: pela largura da caixa, que é o que muda entre telas.
  useLayoutEffect(() => {
    const el = caixa.current;
    if (!el) return;
    const medir = (): void => {
      const largura = el.clientWidth;
      if (largura > 0) definirVertical(largura < 640);
    };
    medir();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", medir);
      return () => window.removeEventListener("resize", medir);
    }
    const observador = new ResizeObserver(medir);
    observador.observe(el);
    return () => observador.disconnect();
  }, []);

  const chaveDe = (lado: Lado, vaga: number): string => `${lado}:${String(vaga)}`;
  const fichaDe = (id: string | null): Ficha | undefined =>
    id === null ? undefined : fichas.current.find((f) => f.id === id && !f.fora);
  const ladoDe = (id: string | null): Lado | null => fichaDe(id)?.lado ?? null;
  const ehGoleiro = (ficha: Ficha): boolean => partidaRef.current[ficha.lado].slots[ficha.vaga] === "GOL";
  const direita = (lado: Lado): boolean => (lado === "home") !== segundoTempo.current;
  const posBase = (ficha: Ficha): Ponto => orientar(basesRef.current[ficha.lado][ficha.vaga] ?? MEIO, direita(ficha.lado));

  /** Os que jogam mais à frente num lado, fora o goleiro, pela formação. */
  function maisAdiantadosDoLado(lado: Lado, quantos: number): Ficha[] {
    const slots = partidaRef.current[lado].slots;
    const bases = basesRef.current[lado];
    return fichas.current
      .filter((f) => f.lado === lado && !f.fora && f.id !== null && slots[f.vaga] !== "GOL")
      .sort((a, b) => (bases[b.vaga]?.x ?? 0) - (bases[a.vaga]?.x ?? 0))
      .slice(0, quantos);
  }

  /**
   * A troca de lado do intervalo. Uma vez só: o intervalo da página e a ação
   * do roteiro chamam os dois, e o segundo não desfaz o primeiro.
   *
   * Os times trocam de lado no vestiário, não correndo pelo campo: cada um
   * aparece na formação do outro lado, a bola no meio, e os dois mais
   * adiantados de cada lado perto do círculo central, cada dupla no próprio
   * campo. Quem sai com a bola o motor diz no primeiro trecho do segundo
   * tempo; a dupla dele anda os últimos metros e dá o passe.
   */
  function trocarDeLado(): void {
    const b = bola.current;
    b.x = MEIO.x;
    b.y = MEIO.y;
    b.dono = null;
    b.voo = null;
    b.altura = 0;
    celebrando.current = null;
    if (segundoTempo.current) return;
    segundoTempo.current = true;
    for (const f of fichas.current) {
      const base = posBase(f);
      f.x = base.x;
      f.y = base.y;
      f.alvoDeCorrida = null;
      f.alvoDaAcao = null;
    }
    for (const lado of ["home", "away"] as const) {
      maisAdiantadosDoLado(lado, 2).forEach((f, i) => {
        const em = orientar({ x: MEIO.x - 4 - i * 3, y: MEIO.y + (i === 0 ? -1.5 : 2.5) }, direita(lado));
        f.x = em.x;
        f.y = em.y;
      });
    }
  }

  // O intervalo da página: a troca de lado acontece enquanto a tela mostra a
  // substituição, e só depois de o primeiro tempo inteiro ter sido desenhado.
  useEffect(() => {
    if (intervalo && levas.current.length === 0 && emCurso.current === null) trocarDeLado();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intervalo]);

  // As fichas nascem da escalação e acompanham quem entra e sai.
  useLayoutEffect(() => {
    const pendentes = new Set<string>();
    for (const leva of levas.current) {
      for (const acao of leva.acoes) {
        if (acao.tipo === "troca") pendentes.add(acao.sai);
        if (acao.tipo === "sai") pendentes.add(acao.quem);
      }
    }
    const novas: Ficha[] = [];
    for (const lado of ["home", "away"] as const) {
      partida[lado].emCampo.forEach((vaga, i) => {
        const chave = chaveDe(lado, i);
        const antiga = fichas.current.find((f) => f.chave === chave);
        if (!antiga) {
          const base = orientar(basesRef.current[lado][i] ?? MEIO, ataqueParaDireita(lado, partida.minute));
          novas.push({
            chave,
            lado,
            vaga: i,
            id: vaga.jogador.id,
            x: base.x,
            y: base.y,
            alvoDaAcao: null,
            alvoDeCorrida: null,
            dx: 0,
            dy: 0,
            marca: null,
            marcaAte: 0,
            fora: vaga.sentOff,
          });
          return;
        }
        // Quem a vista diz que está na vaga é quem fica, a não ser que o
        // roteiro ainda vá mostrar a entrada dele.
        if (antiga.id !== vaga.jogador.id && !(antiga.id && pendentes.has(antiga.id))) antiga.id = vaga.jogador.id;
        if (vaga.sentOff && !antiga.fora && !(antiga.id && pendentes.has(antiga.id))) antiga.fora = true;
        novas.push(antiga);
      });
    }
    fichas.current = novas;
  });

  // Lances novos viram roteiro na fila.
  useEffect(() => {
    const todos = partida.lances;
    if (todos.length < vistos.current) {
      vistos.current = 0;
      levas.current = [];
      emCurso.current = null;
      roteiro.current = estadoInicial();
      segundoTempo.current = false;
      bola.current = { x: MEIO.x, y: MEIO.y, dono: null, voo: null, altura: 0 };
    }
    const novos = todos.slice(vistos.current);
    vistos.current = todos.length;
    if (novos.length === 0) return;

    const acoes = roteiroDosLances(partida, novos, roteiro.current);
    if (velocidade <= 0) {
      // Sem tempo para desenhar: cada ação vale pelo fim dela.
      for (const leva of levas.current) for (const acao of leva.acoes) aplicarNaHora(acao);
      levas.current = [];
      emCurso.current = null;
      for (const acao of acoes) aplicarNaHora(acao);
      for (const ficha of fichas.current) {
        const alvo = alvoDaFormacao(ficha);
        ficha.x = alvo.x;
        ficha.y = alvo.y;
      }
      if (bola.current.dono) {
        const dono = fichaDe(bola.current.dono);
        if (dono) {
          bola.current.x = dono.x;
          bola.current.y = dono.y;
        }
      }
      desenhando.current = false;
      aoDesenharRef.current?.(false);
      redesenhar();
      return;
    }
    levas.current.push({ acoes, escala: null });
    // Desenhando desde já, não só quando o primeiro quadro começar: no meio
    // desse quadro a página ainda acharia a leva pronta e abriria o intervalo
    // com cinco minutos de jogo por desenhar.
    desenhando.current = true;
    aoDesenharRef.current?.(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partida.lances.length, velocidade]);

  /** O fim de uma ação, sem desenhar: para o "pular" e para o que ficou para trás. */
  function aplicarNaHora(acao: Acao): void {
    const b = bola.current;
    switch (acao.tipo) {
      case "recolhe":
      case "desarme":
        b.dono = acao.quem;
        b.voo = null;
        break;
      case "passe":
        b.dono = acao.para;
        b.voo = null;
        break;
      case "conduz": {
        const ficha = fichaDe(acao.quem);
        if (ficha) {
          const alvo = alvoDaConducao(ficha, acao);
          ficha.x = alvo.x;
          ficha.y = alvo.y;
        }
        b.dono = acao.quem;
        b.voo = null;
        break;
      }
      case "corre": {
        const ficha = fichaDe(acao.quem);
        if (ficha && acao.ate !== "bola") {
          ficha.x = acao.ate.x;
          ficha.y = acao.ate.y;
        }
        break;
      }
      case "posiciona": {
        const ficha = fichaDe(acao.quem);
        if (ficha) {
          ficha.x = acao.em.x;
          ficha.y = acao.em.y;
          ficha.alvoDeCorrida = null;
        }
        break;
      }
      case "coloca":
        b.x = acao.em.x;
        b.y = acao.em.y;
        b.dono = null;
        b.voo = null;
        b.altura = 0;
        break;
      case "chute":
        b.voo = null;
        b.x = acao.alvo.x;
        b.y = acao.alvo.y;
        b.dono = acao.desfecho === "defesa" ? acao.goleiro : null;
        break;
      case "sai": {
        const ficha = fichaDe(acao.quem);
        if (ficha) ficha.fora = true;
        if (b.dono === acao.quem) b.dono = null;
        break;
      }
      case "troca":
        trocar(acao.sai, acao.entra, false);
        break;
      case "intervalo":
        trocarDeLado();
        break;
      case "reinicio":
        b.x = MEIO.x;
        b.y = MEIO.y;
        b.dono = null;
        b.voo = null;
        voltando.current = false;
        break;
      case "falta":
      case "impedimento":
        b.dono = null;
        b.voo = null;
        b.altura = 0;
        break;
      case "lance":
        aoMostrarRef.current?.(acao.lance);
        break;
      default:
        break;
    }
  }

  function trocar(sai: string, entra: string, animar: boolean): void {
    const ficha = fichaDe(sai);
    if (!ficha) return;
    ficha.id = entra;
    if (animar) {
      // Quem entra vem da lateral.
      ficha.y = ficha.y < MEIO.y ? -2 : CAMPO.altura + 2;
      ficha.marca = "entra";
      ficha.marcaAte = tempo.current + 2.5;
    }
    if (bola.current.dono === sai) bola.current.dono = entra;
    redesenhar();
  }

  function alvoDaConducao(ficha: Ficha, acao: Extract<Acao, { tipo: "conduz" }>): Ponto {
    if (acao.ate) return acao.ate;
    const dir = direita(ficha.lado) ? 1 : -1;
    return {
      x: limitar(ficha.x + dir * acao.avanco, 2, CAMPO.largura - 2),
      y: limitar(ficha.y + acao.lateral, 2, CAMPO.altura - 2),
    };
  }

  /** Quem tem a bola, ou para quem ela está indo: o lado que está com a posse. */
  function ladoComABola(): Lado | null {
    const b = bola.current;
    if (b.dono) return ladoDe(b.dono);
    if (b.voo && !("x" in b.voo.para)) return ladoDe(b.voo.para.id);
    return null;
  }

  /** Onde a formação põe a ficha agora: a base, o bloco que sobe ou recua, e a bola que puxa. */
  function alvoDaFormacao(ficha: Ficha): Ponto {
    const base = posBase(ficha);
    const b = bola.current;
    const dir = direita(ficha.lado) ? 1 : -1;
    if (ehGoleiro(ficha)) {
      return {
        x: limitar(base.x + (b.x - MEIO.x) * 0.04, 1.5, CAMPO.largura - 1.5),
        y: limitar(base.y + (b.y - MEIO.y) * 0.16, MEIO.y - 4.5, MEIO.y + 4.5),
      };
    }
    const posse = ladoComABola();
    const avanco = posse === null ? 0 : posse === ficha.lado ? 8 : -5;
    return {
      x: limitar(base.x + dir * avanco + (b.x - MEIO.x) * 0.22, 1.5, CAMPO.largura - 1.5),
      y: limitar(base.y + (b.y - MEIO.y) * 0.22, 1.5, CAMPO.altura - 1.5),
    };
  }

  /** Quanto tempo de jogo o roteiro deve levar, pelo que está em campo agora. */
  function duracaoNatural(acoes: Acao[]): number {
    let total = 0;
    const posicoes = new Map<string, Ponto>();
    for (const f of fichas.current) if (f.id) posicoes.set(f.id, { x: f.x, y: f.y });
    let ondeBola: Ponto = { x: bola.current.x, y: bola.current.y };
    const em = (id: string): Ponto => posicoes.get(id) ?? ondeBola;
    for (const acao of acoes) {
      switch (acao.tipo) {
        case "recolhe":
        case "desarme":
          total += dist(em(acao.quem), ondeBola) / VEL.corre + 0.3;
          ondeBola = em(acao.quem);
          break;
        case "passe": {
          const d = dist(em(acao.de), em(acao.para));
          total += Math.max(0.35, d / (acao.alto ? VEL.passeAlto : VEL.passe)) + 0.25;
          ondeBola = em(acao.para);
          break;
        }
        case "conduz": {
          const de = em(acao.quem);
          const ate = acao.ate ?? { x: de.x + acao.avanco, y: de.y + acao.lateral };
          total += dist(de, ate) / VEL.conduz + 0.15;
          posicoes.set(acao.quem, ate);
          ondeBola = ate;
          break;
        }
        case "chute":
          total += dist(ondeBola, acao.alvo) / VEL.chute + 0.3;
          ondeBola = acao.alvo;
          break;
        case "espera":
          total += acao.s;
          break;
        case "reinicio":
          ondeBola = { ...MEIO };
          total += 4;
          break;
        case "intervalo":
          ondeBola = { ...MEIO };
          total += 0.2;
          break;
        case "coloca":
          ondeBola = acao.em;
          break;
        case "falta":
          total += dist(em(acao.quem), em(acao.sobre)) / VEL.corre + 0.4;
          break;
        default:
          break;
      }
    }
    return Math.max(0.5, total);
  }

  /** Começa a ação da vez. Devolve falso quando ela é instantânea e já acabou. */
  function iniciar(acao: Acao, agora: number): boolean {
    const b = bola.current;
    switch (acao.tipo) {
      case "recolhe":
      case "desarme": {
        const ficha = fichaDe(acao.quem);
        if (!ficha) return false;
        emCurso.current = { acao, inicio: agora, limite: agora + 4 };
        return true;
      }
      case "passe": {
        const de = fichaDe(acao.de);
        const para = fichaDe(acao.para);
        if (!de || !para) {
          if (para) b.dono = acao.para;
          return false;
        }
        if (b.dono !== acao.de) {
          b.x = de.x;
          b.y = de.y;
        }
        b.dono = null;
        const d = dist({ x: b.x, y: b.y }, para);
        b.voo = {
          de: { x: b.x, y: b.y },
          para: { id: acao.para },
          t: 0,
          dur: Math.max(0.35, d / (acao.alto ? VEL.passeAlto : VEL.passe)),
          alto: acao.alto,
          chute: null,
        };
        emCurso.current = { acao, inicio: agora, limite: agora + b.voo.dur + 3 };
        return true;
      }
      case "conduz": {
        const ficha = fichaDe(acao.quem);
        if (!ficha) return false;
        if (b.dono !== acao.quem) {
          b.dono = acao.quem;
          b.voo = null;
        }
        ficha.alvoDaAcao = alvoDaConducao(ficha, acao);
        emCurso.current = { acao, inicio: agora, limite: agora + dist(ficha, ficha.alvoDaAcao) / VEL.conduz + 1.2 };
        return true;
      }
      case "corre": {
        const ficha = fichaDe(acao.quem);
        if (ficha) ficha.alvoDeCorrida = acao.ate;
        return false;
      }
      case "posiciona":
      case "coloca":
        aplicarNaHora(acao);
        return false;
      case "chute": {
        const ficha = fichaDe(acao.quem);
        if (!ficha) {
          aplicarNaHora(acao);
          return false;
        }
        if (b.dono !== acao.quem) {
          b.x = ficha.x;
          b.y = ficha.y;
        }
        b.dono = null;
        const d = dist({ x: b.x, y: b.y }, acao.alvo);
        b.voo = {
          de: { x: b.x, y: b.y },
          para: acao.alvo,
          t: 0,
          dur: Math.max(0.25, d / (acao.alto ? VEL.chuteAlto : VEL.chute)),
          alto: acao.alto,
          chute: acao,
        };
        if (acao.desfecho === "defesa" && acao.goleiro) {
          const goleiro = fichaDe(acao.goleiro);
          if (goleiro) goleiro.alvoDeCorrida = { x: acao.alvo.x, y: acao.alvo.y };
        }
        emCurso.current = { acao, inicio: agora, limite: agora + b.voo.dur + 2 };
        return true;
      }
      case "espera":
        emCurso.current = { acao, inicio: agora, limite: agora + acao.s };
        return true;
      case "marca": {
        const ficha = fichaDe(acao.quem);
        if (ficha) {
          ficha.marca = acao.marca;
          ficha.marcaAte = agora + 2.5;
        }
        if (acao.marca === "gol") celebrando.current = { id: acao.quem, ate: agora + 1.6 };
        return false;
      }
      case "sai": {
        const ficha = fichaDe(acao.quem);
        if (ficha) ficha.fora = true;
        if (b.dono === acao.quem) b.dono = null;
        return false;
      }
      case "troca":
        trocar(acao.sai, acao.entra, true);
        return false;
      case "intervalo":
        aplicarNaHora(acao);
        celebrando.current = null;
        for (const f of fichas.current) {
          f.alvoDeCorrida = null;
          f.alvoDaAcao = null;
        }
        return false;
      case "reinicio": {
        // Bola no meio e todo mundo de volta à formação. A ação só termina
        // quando cada um chegou, ou quando a espera passa do razoável.
        aplicarNaHora(acao);
        celebrando.current = null;
        for (const f of fichas.current) f.alvoDaAcao = null;
        voltando.current = true;
        emCurso.current = { acao, inicio: agora, limite: agora + 9 };
        return true;
      }
      case "falta": {
        const quem = fichaDe(acao.quem);
        const sobre = fichaDe(acao.sobre);
        if (!quem || !sobre) return false;
        quem.alvoDaAcao = { x: sobre.x, y: sobre.y };
        emCurso.current = { acao, inicio: agora, limite: agora + 4 };
        return true;
      }
      case "impedimento": {
        const quem = fichaDe(acao.quem);
        aplicarNaHora(acao);
        if (quem) {
          quem.marca = "impedimento";
          quem.marcaAte = agora + 2.5;
          quem.alvoDeCorrida = null;
        }
        return false;
      }
      case "lance":
        aoMostrarRef.current?.(acao.lance);
        return false;
      default:
        return false;
    }
  }

  /** A ação em curso acabou? Aplica o fim quando sim. */
  function terminou(curso: AcaoEmCurso, agora: number): boolean {
    const b = bola.current;
    const { acao } = curso;
    const estourou = agora >= curso.limite;
    switch (acao.tipo) {
      case "recolhe": {
        const ficha = fichaDe(acao.quem);
        if (!ficha) return true;
        if (estourou || dist(ficha, b) < 1.1) {
          b.dono = acao.quem;
          b.voo = null;
          ficha.alvoDaAcao = null;
          return true;
        }
        ficha.alvoDaAcao = { x: b.x, y: b.y };
        return false;
      }
      case "desarme": {
        const ficha = fichaDe(acao.quem);
        if (!ficha) return true;
        const dono = fichaDe(b.dono);
        const alvo: Ponto = dono ?? { x: b.x, y: b.y };
        if (estourou || dist(ficha, alvo) < 1.4) {
          b.dono = acao.quem;
          b.voo = null;
          ficha.alvoDaAcao = null;
          return true;
        }
        ficha.alvoDaAcao = alvo;
        return false;
      }
      case "passe": {
        if (b.voo === null) return true;
        if (estourou) {
          b.voo = null;
          b.dono = acao.para;
          return true;
        }
        return false;
      }
      case "conduz": {
        const ficha = fichaDe(acao.quem);
        if (!ficha || !ficha.alvoDaAcao) return true;
        if (estourou || dist(ficha, ficha.alvoDaAcao) < 0.9) {
          ficha.alvoDaAcao = null;
          return true;
        }
        return false;
      }
      case "chute": {
        if (b.voo === null) return true;
        if (estourou) {
          aplicarNaHora(acao);
          return true;
        }
        return false;
      }
      case "espera":
        return estourou;
      case "reinicio": {
        const todos = fichas.current.every((f) => f.fora || (f.alvoDeCorrida === null && dist(f, posBase(f)) < 3));
        if (estourou || todos) {
          voltando.current = false;
          return true;
        }
        return false;
      }
      case "falta": {
        const quem = fichaDe(acao.quem);
        const sobre = fichaDe(acao.sobre);
        if (!quem || !sobre) return true;
        quem.alvoDaAcao = { x: sobre.x, y: sobre.y };
        if (estourou || dist(quem, sobre) < 1.6) {
          aplicarNaHora(acao);
          quem.alvoDaAcao = null;
          quem.marca = "falta";
          quem.marcaAte = agora + 2.2;
          sobre.alvoDaAcao = null;
          return true;
        }
        return false;
      }
      default:
        return true;
    }
  }

  /** A bola chegou ao fim do voo. */
  function chegou(voo: Voo): void {
    const b = bola.current;
    b.voo = null;
    b.altura = 0;
    if (voo.chute) {
      const acao = voo.chute;
      b.x = acao.alvo.x;
      b.y = acao.alvo.y;
      b.dono = acao.desfecho === "defesa" ? acao.goleiro : null;
      if (acao.desfecho === "defesa" && acao.goleiro) {
        const goleiro = fichaDe(acao.goleiro);
        if (goleiro) goleiro.alvoDeCorrida = null;
      }
      return;
    }
    if ("id" in voo.para) b.dono = voo.para.id;
  }

  // Em desenvolvimento, o estado fica visível para o roteiro de conferência.
  useEffect(() => {
    if (!import.meta.env.DEV) return;
    const janela = window as unknown as { __pf?: unknown };
    janela.__pf = {
      estado: () => ({
        bola: { ...bola.current, voo: bola.current.voo ? { ...bola.current.voo, t: Number(bola.current.voo.t.toFixed(2)) } : null },
        acao: emCurso.current?.acao ?? null,
        fila: levas.current.map((l) => ({ n: l.acoes.length, escala: l.escala })),
        fichas: fichas.current.map((f) => ({ chave: f.chave, id: f.id, x: Number(f.x.toFixed(1)), y: Number(f.y.toFixed(1)), corre: f.alvoDeCorrida, acao: f.alvoDaAcao, fora: f.fora })),
        tempo: Number(tempo.current.toFixed(1)),
        segundoTempo: segundoTempo.current,
      }),
    };
    return () => {
      delete janela.__pf;
    };
  }, []);

  // O relógio do desenho.
  useEffect(() => {
    let vivo = true;
    let ultimo = performance.now();
    let quadroId = 0;

    const desenhar = (): void => {
      const v = verticalRef.current;
      const naTela = (p: Ponto): Ponto => (v ? { x: p.y, y: CAMPO.largura - p.x } : p);
      const b = bola.current;
      for (const ficha of fichas.current) {
        const el = elementos.current.get(ficha.chave);
        if (!el) continue;
        const p = naTela({ x: ficha.x, y: ficha.y });
        el.setAttribute("transform", `translate(${p.x.toFixed(2)} ${p.y.toFixed(2)})`);
        el.style.opacity = ficha.fora ? "0" : "1";
        const marca = ficha.marca && ficha.marcaAte > tempo.current ? ficha.marca : "";
        if (el.dataset.marca !== marca) el.dataset.marca = marca;
        const comBola = ficha.id !== null && b.dono === ficha.id;
        if (comBola && !el.hasAttribute("data-bola")) el.setAttribute("data-bola", "");
        if (!comBola && el.hasAttribute("data-bola")) el.removeAttribute("data-bola");
      }
      if (bolaEl.current) {
        const p = naTela({ x: b.x, y: b.y });
        bolaEl.current.setAttribute("transform", `translate(${p.x.toFixed(2)} ${p.y.toFixed(2)})`);
        const escala = 1 + b.altura * 0.16;
        bolaCirc.current?.setAttribute("r", (0.8 * escala).toFixed(2));
        bolaCirc.current?.setAttribute("cy", (-b.altura * 0.6).toFixed(2));
        bolaSombra.current?.setAttribute("rx", (0.9 * (1 + b.altura * 0.1)).toFixed(2));
        bolaSombra.current?.setAttribute("cy", (0.9 + b.altura * 0.25).toFixed(2));
      }
    };

    const mover = (ficha: Ficha, alvo: Ponto, vel: number, dtj: number): void => {
      const d = dist(ficha, alvo);
      if (d < 0.05) return;
      const passo = Math.min(d, vel * dtj);
      const nx = (alvo.x - ficha.x) / d;
      const ny = (alvo.y - ficha.y) / d;
      ficha.x += nx * passo;
      ficha.y += ny * passo;
      ficha.dx = nx;
      ficha.dy = ny;
    };

    const quadro = (agoraReal: number): void => {
      if (!vivo) return;
      const dtReal = Math.min(0.08, (agoraReal - ultimo) / 1000);
      ultimo = agoraReal;

      // A leva da vez ganha a escala de tempo na hora em que começa: a
      // velocidade escolhida, ou mais se houver um orçamento a cumprir.
      let leva = levas.current[0] ?? null;
      if (leva && leva.escala === null) {
        const natural = duracaoNatural(leva.acoes);
        const base = Math.max(0.25, velocidadeRef.current);
        const orcamento = orcamentoRef.current;
        leva.escala = orcamento ? limitar(natural / Math.max(0.2, orcamento / 1000), base, 6) : base;
        aoDesenharRef.current?.(true, (natural / leva.escala) * 1000);
      } else if (leva && !orcamentoRef.current) {
        // O ritmo trocado vale na hora, e não só no trecho seguinte: de 1x
        // para 20x, o trecho em curso ainda levava meio minuto. O relógio da
        // página recebe o que falta, no ritmo novo.
        const base = Math.max(0.25, velocidadeRef.current);
        if (base !== leva.escala) {
          leva.escala = base;
          aoDesenharRef.current?.(true, (duracaoNatural(leva.acoes) / base) * 1000);
        }
      }
      const escala = leva?.escala ?? 1;
      const dtj = dtReal * escala;
      const agora = tempo.current + dtj;
      tempo.current = agora;

      // As ações: instantâneas passam na hora; as demais duram o que precisam.
      let guarda = 0;
      while (leva && guarda < 50) {
        guarda += 1;
        if (emCurso.current) {
          if (!terminou(emCurso.current, agora)) break;
          emCurso.current = null;
          leva.acoes.shift();
        }
        const proxima = leva.acoes[0];
        if (!proxima) {
          levas.current.shift();
          leva = levas.current[0] ?? null;
          if (leva === null && desenhando.current) {
            desenhando.current = false;
            aoDesenharRef.current?.(false);
            // O intervalo chegou enquanto o primeiro tempo ainda era desenhado.
            if (intervaloRef.current) trocarDeLado();
          }
          continue;
        }
        if (iniciar(proxima, agora)) break;
        leva.acoes.shift();
      }

      const b = bola.current;
      const parada = leva === null && paradoRef.current;

      if (!parada) {
        // A bola em voo.
        if (b.voo) {
          const voo = b.voo;
          voo.t += dtj;
          const p = Math.min(1, voo.t / voo.dur);
          const suave = 1 - Math.pow(1 - p, 1.5);
          const alvo: Ponto = "id" in voo.para ? (fichaDe(voo.para.id) ?? voo.de) : voo.para;
          b.x = voo.de.x + (alvo.x - voo.de.x) * suave;
          b.y = voo.de.y + (alvo.y - voo.de.y) * suave;
          b.altura = voo.alto ? Math.sin(Math.PI * p) * 3 : voo.chute ? Math.sin(Math.PI * p) * 0.6 : 0;
          if (p >= 1) chegou(voo);
        }

        // Quem está mais perto de quem tem a bola vai pressionar.
        const dono = fichaDe(b.dono);
        let pressao: Ficha | null = null;
        if (dono) {
          let menor = 22;
          for (const f of fichas.current) {
            if (f.fora || f.lado === dono.lado || ehGoleiro(f)) continue;
            const d = dist(f, dono);
            if (d < menor) {
              menor = d;
              pressao = f;
            }
          }
        }

        const festa = celebrando.current && celebrando.current.ate > agora ? celebrando.current : null;
        const artilheiro = festa ? fichaDe(festa.id) : undefined;

        for (const ficha of fichas.current) {
          if (ficha.fora) continue;
          const goleiro = ehGoleiro(ficha);
          let alvo: Ponto;
          let vel: number = goleiro ? VEL.goleiro : VEL.corre;
          if (ficha.alvoDaAcao) {
            alvo = ficha.alvoDaAcao;
            if (b.dono === ficha.id) vel = VEL.conduz;
          } else if (ficha.alvoDeCorrida) {
            alvo = ficha.alvoDeCorrida === "bola" ? { x: b.x, y: b.y } : ficha.alvoDeCorrida;
            if (dist(ficha, alvo) < 0.8) {
              ficha.alvoDeCorrida = null;
              alvo = alvoDaFormacao(ficha);
            }
          } else if (voltando.current) {
            alvo = posBase(ficha);
          } else if (festa && artilheiro && ficha.lado === artilheiro.lado && ficha.id !== artilheiro.id && !goleiro) {
            alvo = {
              x: artilheiro.x + (ficha.y < artilheiro.y ? -2 : 2),
              y: artilheiro.y + (ficha.x < artilheiro.x ? -2 : 2),
            };
          } else if (b.voo && "id" in b.voo.para && b.voo.para.id === ficha.id) {
            const base = alvoDaFormacao(ficha);
            alvo = { x: base.x + (b.x - base.x) * 0.35, y: base.y + (b.y - base.y) * 0.35 };
          } else if (pressao === ficha && dono) {
            alvo = dist(ficha, dono) < 1.8 ? ficha : dono;
          } else if (b.dono === ficha.id) {
            // Com a bola e sem ordem: segura a posição, andando devagar.
            const base = alvoDaFormacao(ficha);
            alvo = { x: (ficha.x + base.x) / 2, y: (ficha.y + base.y) / 2 };
            vel = VEL.conduz * 0.6;
          } else {
            alvo = alvoDaFormacao(ficha);
          }
          mover(ficha, alvo, vel, dtj);
        }

        // A bola no pé de quem a tem: um pouco à frente, na direção do passo.
        if (dono && !b.voo) {
          const dir = direita(dono.lado) ? 1 : -1;
          const parado = dono.dx === 0 && dono.dy === 0;
          const nx = parado ? dir : dono.dx;
          const ny = parado ? 0 : dono.dy;
          b.x = dono.x + nx * 1.1;
          b.y = dono.y + ny * 1.1;
          b.altura = 0;
        }
      }

      desenhar();
      quadroId = requestAnimationFrame(quadro);
    };
    quadroId = requestAnimationFrame(quadro);
    return () => {
      vivo = false;
      cancelAnimationFrame(quadroId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const viewBox = vertical ? "-4 -6 76 117" : "-6 -4 117 76";

  return (
    <div className="pf-campo" ref={caixa} data-vertical={vertical ? "" : undefined}>
      <svg viewBox={viewBox} preserveAspectRatio="xMidYMid meet" aria-hidden="true">
        <defs>
          <pattern id="pf-rede" width="0.8" height="0.8" patternUnits="userSpaceOnUse">
            <path d="M0 0H0.8M0 0V0.8" stroke="#fff" strokeOpacity="0.35" strokeWidth="0.08" />
          </pattern>
        </defs>
        <g transform={vertical ? "matrix(0 -1 1 0 0 105)" : undefined}>
          <rect x="-6" y="-6" width="117" height="80" fill="#0f3f21" />
          {Array.from({ length: 10 }, (_, i) => (
            <rect key={i} x={i * 10.5} y={0} width={10.5} height={68} fill={i % 2 ? "#1a6130" : "#1d6b33"} />
          ))}
          <g fill="none" stroke="#fff" strokeOpacity="0.6" strokeWidth="0.35">
            <rect x="0" y="0" width="105" height="68" />
            <line x1="52.5" y1="0" x2="52.5" y2="68" />
            <circle cx="52.5" cy="34" r="9.15" />
            <rect x="0" y="13.85" width="16.5" height="40.3" />
            <rect x="88.5" y="13.85" width="16.5" height="40.3" />
            <rect x="0" y="24.85" width="5.5" height="18.3" />
            <rect x="99.5" y="24.85" width="5.5" height="18.3" />
            <path d="M16.5 26.69A9.15 9.15 0 0 1 16.5 41.31" />
            <path d="M88.5 26.69A9.15 9.15 0 0 0 88.5 41.31" />
          </g>
          <g fill="#fff" fillOpacity="0.6">
            <circle cx="11" cy="34" r="0.45" />
            <circle cx="52.5" cy="34" r="0.45" />
            <circle cx="94" cy="34" r="0.45" />
          </g>
          <rect x="-2.4" y="30.34" width="2.4" height="7.32" fill="url(#pf-rede)" stroke="#fff" strokeWidth="0.3" />
          <rect x="105" y="30.34" width="2.4" height="7.32" fill="url(#pf-rede)" stroke="#fff" strokeWidth="0.3" />
        </g>

        {(["home", "away"] as const).map((lado) =>
          partida[lado].emCampo.map((vaga, i) => {
            const chave = chaveDe(lado, i);
            const ficha = fichas.current.find((f) => f.chave === chave);
            const id = ficha?.id ?? vaga.jogador.id;
            const jogador = partida[lado].emCampo.find((v) => v.jogador.id === id)?.jogador ?? vaga.jogador;
            const goleiro = partida[lado].slots[i] === "GOL";
            const cores = partida[lado].colors;
            const fundo = goleiro ? GOLEIRO[lado].fundo : cores[0];
            const borda = goleiro ? GOLEIRO[lado].borda : cores[1] === cores[0] ? "#ffffff" : cores[1];
            const texto = goleiro ? GOLEIRO[lado].texto : textoSobre(cores[0]);
            return (
              <g
                key={chave}
                className="pf-ficha"
                data-lado={lado}
                data-minha={partida[lado].clubId === partida.myClubId ? "" : undefined}
                ref={(el) => {
                  if (el) elementos.current.set(chave, el);
                  else elementos.current.delete(chave);
                }}
              >
                <title>{`${jogador.shirt === null ? "" : String(jogador.shirt) + " "}${jogador.name} · ${partida[lado].slots[i] ?? jogador.pos}`}</title>
                <ellipse cx="0.3" cy="1.9" rx="1.6" ry="0.55" fill="rgba(0,0,0,0.35)" />
                <circle className="pf-posse" r="2.5" fill="none" stroke="#ffffff" strokeOpacity="0.85" strokeWidth="0.28" />
                <circle r="1.75" fill={fundo} stroke={borda} strokeWidth="0.32" />
                <text y="0.72" fontSize="1.95" textAnchor="middle" fill={texto}>
                  {jogador.shirt ?? ""}
                </text>
                <g className="pf-marca pf-marca-amarelo"><rect x="1.2" y="-3.4" width="1.3" height="1.8" rx="0.2" fill="#f5c542" stroke="#1a1200" strokeWidth="0.12" /></g>
                <g className="pf-marca pf-marca-vermelho"><rect x="1.2" y="-3.4" width="1.3" height="1.8" rx="0.2" fill="#e5484d" stroke="#3a0a0c" strokeWidth="0.12" /></g>
                <g className="pf-marca pf-marca-lesao"><path d="M-3.4 -2.6h1.6M-2.6 -3.4v1.6" stroke="#e5484d" strokeWidth="0.5" strokeLinecap="round" /></g>
                <g className="pf-marca pf-marca-gol"><circle r="2.6" fill="none" stroke="#2fe06a" strokeWidth="0.35" /></g>
                <g className="pf-marca pf-marca-entra"><path d="M-3.2 -2.2l0.9 -1.2l0.9 1.2" fill="none" stroke="#2fe06a" strokeWidth="0.4" strokeLinecap="round" /></g>
                <g className="pf-marca pf-marca-falta"><circle cx="2.4" cy="-2.6" r="1" fill="#fff" stroke="#1a1a1a" strokeWidth="0.15" /><path d="M1.8 -3.2l1.2 1.2M3 -3.2l-1.2 1.2" stroke="#1a1a1a" strokeWidth="0.3" strokeLinecap="round" /></g>
                <g className="pf-marca pf-marca-impedimento"><path d="M2.2 -1.2v-3.2" stroke="#fff" strokeWidth="0.3" /><path d="M2.2 -4.4h2l-0.6 0.9l0.6 0.9h-2z" fill="#f2a33a" /></g>
              </g>
            );
          }),
        )}

        <g className="pf-bola" ref={bolaEl}>
          <ellipse ref={bolaSombra} cx="0.3" cy="0.9" rx="0.9" ry="0.35" fill="rgba(0,0,0,0.4)" />
          <circle ref={bolaCirc} r="0.8" fill="#fff" stroke="#1a1a1a" strokeWidth="0.18" />
        </g>
      </svg>
    </div>
  );
}
